import React, { useEffect, useState, useMemo } from "react";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Typography,
  Box
} from '@mui/material';
import DialogDetalleRemito from "./DialogDetalleRemito"
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import { putData } from "../../../services/library";
import { useToast } from "../../../Hooks/UseToast";
import EstadoChip from "./EstadoChip";
import * as apiRemitos from "../api"
import DialogEditarRemito from "./DialogEditarRemito";
import RemitoDetailPanel from "./RemitoDetailPanel";

const BRAND = {
  main: "#2471A3",
  dark: "#1B4F72",
  light: "#EAF2F8",
  accent: "#F39C12",
  surface: "#F4F7F9",
  border: "#E1E8ED",
};

function AllRemitosTable({ remitos, setRemitos }) {
  const navigate = useNavigate();

  const [selectedRemito, setSelectedRemito] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  const handleOpenViewModal = (remito) => {
    setSelectedRemito(remito);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setSelectedRemito(null);
    setOpenViewModal(false);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "nro",
        header: "N°",
        size: 70,
        enableColumnFilter: true,
      },
      {
        id: "fecha",
        header: "Fecha",
        size: 90,
        enableColumnFilter: true,
        accessorFn: (row) => row.fecha,
        Cell: ({ cell }) => (cell.getValue() ? moment(cell.getValue()).format("DD-MM-YYYY") : ""),
        sortingFn: "datetime",
      },
      {
        accessorKey: "empresa",
        header: "Empresa",
        size: 80,
      },
      {
        accessorKey: "sucursal",
        id: "sucursal",
        header: "Suc. Remite",
        size: 100,
      },
      {
        accessorKey: "cliente",
        header: "Cliente",
        size: 180,
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 80,
        Cell: ({ row }) => (
          <EstadoChip estado={row.original.estado} />
        ),
      },
    ],
    []
  );

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const handleEditRemito = (remito) => {
    setSelectedRemito(remito)
    setOpenEditDialog(true)
  };

  const handleAnularRemito = async (remito) => {
    if (!window.confirm(`¿Desea anular el remito N° ${remito.nro}?`)) return;
    await apiRemitos.putRemitos(remito, showToast)
    await apiRemitos.getRemitos(setRemitos)
  };

  const { Toast, showToast } = useToast();

  return (
    <div>
      <MaterialReactTable
        enableRowActions={true}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            size: 100,
            grow: false,
          },
        }}
        positionActionsColumn="last"
        renderDetailPanel={({ row }) => (
          <RemitoDetailPanel remitoId={row.original.id_remito} />
        )}
        muiTableDetailPanelProps={{
          sx: {
            width: "100%",
            display: "table-row", // o "flex" según comportamiento
            "& > td": {
              width: "100%",
              padding: 0,
            },
          },
        }}
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: "0.25" }}>
            <Tooltip title="Ver remito">
              <IconButton
                sx={{ color: "#000" }}
                onClick={() => handleOpenViewModal(row.original)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar remito" disabled={row.original.id_estado !== 1}
            >
              <IconButton
                color="primary"
                onClick={() => handleEditRemito(row.original)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Anular remito" >
              <IconButton
                disabled={row.original.id_estado !== 1}
                color="error"
                onClick={(e) => handleAnularRemito(row.original)}
              >
                <BlockIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        columns={columns}
        data={remitos}
        localization={MRT_Localization_ES}
        enableColumnResizing
        columnResizeMode="onChange"
        enablePinning
        enableColumnFilters={false}
        enableGlobalFilter
        enableHiding={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableStickyHeader
        enableColumnActions={false}
        autoResetPageIndex={false}
        renderTopToolbarCustomActions={() => (
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/ingreso-remitos")}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Nuevo Remito
          </Button>
        )}
        layoutMode="grid"
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          },
        }}
        muiTableContainerProps={{ sx: { maxHeight: "70vh" } }}
        muiTableHeadCellProps={{
          sx: {
            bgcolor: "#f8fafc",
            fontWeight: 700,
            color: "text.primary",
            borderBottom: "1px solid",
            borderColor: "divider",
          },
        }}
        muiTableBodyRowProps={{
          hover: true,
          sx: {
            "&:hover td": { bgcolor: "action.hover" },
          },
        }}
        muiTableBodyCellProps={{ sx: { borderColor: "divider" } }}
        muiSearchTextFieldProps={{
          placeholder: "Buscar en todos los campos...",
          size: "small",
          sx: { minWidth: 280 },
          variant: "outlined",
        }}
        enablePagination
        muiTablePaginationProps={{
          rowsPerPageOptions: [10, 25, 50, 100],
          showFirstButton: true,
          showLastButton: true,
        }}
        initialState={{
          density: "compact",
          showGlobalFilter: true,
          pagination: { pageSize: 10, pageIndex: 0 },
          columnPinning: { left: ["mrt-row-actions"], left: [] },
          sorting: [
            {
              id: "fecha",
              desc: true,
            },
          ],
        }}
      />
      {
        openViewModal && selectedRemito && (
          <DialogDetalleRemito open={openViewModal} onClose={handleCloseViewModal} remito={selectedRemito} />
        )
      }
      {
        openEditDialog && (
          <DialogEditarRemito
            setRemitos={setRemitos}
            open={openEditDialog}
            setOpen={setOpenEditDialog}
            remito={selectedRemito}
            onClose={() => setOpenEditDialog(false)}
            getRemitos={() => apiRemitos.getRemitos(setRemitos)}
          />
        )
      }
      {Toast}
    </div>
  );
}

export default AllRemitosTable;
